export enum MortgageTrackType {
  FIXED_LINKED = "FIXED_LINKED",
  FIXED_UNLINKED = "FIXED_UNLINKED",
  PRIME = "PRIME",
  VARIABLE_UNLINKED_18M = "VARIABLE_UNLINKED_18M",
  VARIABLE_LINKED_3Y = "VARIABLE_LINKED_3Y",
  VARIABLE_UNLINKED_3Y = "VARIABLE_UNLINKED_3Y",
  VARIABLE_LINKED_5Y = "VARIABLE_LINKED_5Y",
  VARIABLE_UNLINKED_5Y = "VARIABLE_UNLINKED_5Y",
  VARIABLE_LINKED_10Y = "VARIABLE_LINKED_10Y",
  VARIABLE_UNLINKED_10Y = "VARIABLE_UNLINKED_10Y",
}

export enum MortgageType {
  UNIFORM_1 = "UNIFORM_1",
  UNIFORM_2 = "UNIFORM_2",
  UNIFORM_3 = "UNIFORM_3",
  CUSTOM = "CUSTOM",
}

export enum MortgageTrackRepaymentType {
    
    SPITZER = "SPITZER",
    BULLET = "BULLET",
    FIXED_PRINCIPAL = "FIXED_PRINCIPAL",
}
export enum MortgageTrackIndexationType {
    NONE = "NONE",
    PRIME = "PRIME",
    CPI = "CPI", //צמוד למדד
    GOVERNMENT_BOND = "GOVERNMENT_BOND",
}
export enum MortgageInterestBaseType {
    FIXED = "FIXED",
    PRIME = "PRIME",
    GOV_BOND = "GOV_BOND",
}

export enum PropertyType {
    UNDER_CONSTRUCTION = "UNDER_CONSTRUCTION", // בית בבניה
    FROM_CONTRACTOR = "FROM_CONTRACTOR", // בית מקבלן
    READY_MADE = "READY_MADE", // בית מוכן
}

export interface MortgageBorrower {
    full_name?: string; // שם מלא של הלווה
    israeli_id?: string; // תעודת זהות (לא נשמר ב-DB, רק לאימות)
}

export interface MortgageTrack {
    track_type: MortgageTrackType;
    amountPercentage?: number;
    amount?: number;
    original_amount?: number; // סכום מקורי של המסלול
    current_balance?: number; // יתרה נוכחית של המסלול
    months?: number;
    interest_rate?: number;
    original_interest_rate?: number; // ריבית מקורית בעת פתיחת המסלול
    repayment_type?: MortgageTrackRepaymentType;
    indexation_type?: MortgageTrackIndexationType; // הצמדת קרן
    interest_base_type?: MortgageInterestBaseType; // בסיס חישוב הריבית
    interest_margin?: number; // מרווח הריביות (למשל פריים מינוס 0.5)
    interest_update_interval_years?: number;
    next_interest_change_date?: string; // מועד שינוי ריבית קרוב (ISO format: YYYY-MM-DD)
    loan_purpose?: string; // מטרת ההלוואה (למשל "רכישת דירה", "שיפוץ")
    monthly_payment?: number; // תשלום חודשי של המסלול
    first_payment_date?: string; // מועד תשלום ראשון של המסלול (ISO format: YYYY-MM-DD)
    expected_end_date?: string; // תאריך סיום צפוי של המסלול / מועד תשלום אחרון (ISO format: YYYY-MM-DD)
}

export interface Mortgage {
    basket_type: MortgageType;
    tracks: MortgageTrack[];
}

/**
 * פרטי משכנתא מלאים למימשק
 * משמש כממשק בין ManualMimshakData לבין MortgageCalculator
 * DTO עסקי - מייצג משכנתא במערכת (ללא נתוני חילוץ)
 */
export interface MortgageDetails {
  mortgage_number?: string; // מספר משכנתא
  property_address?: string; // כתובת הנכס
  total_original_amount?: number; // סכום משכנתא מקורי כולל
  total_current_balance?: number; // יתרה כוללת נוכחית
  start_date?: string; // תאריך התחלת המשכנתא (ISO format: YYYY-MM-DD)
  end_date?: string; // תאריך סיום המשכנתא (ISO format: YYYY-MM-DD)
  report_date?: string; // תאריך הדוח / מועד חישוב (ISO format: YYYY-MM-DD)
  loan_purpose?: string; // מטרת ההלוואה הכללית (למשל "רכישת דירה", "שיפוץ")
  tracks?: MortgageTrack[]; // מסלולי המשכנתא
  bank_code?: string; // קוד בנק
  bank_name?: string; // שם בנק
  monthly_payment_total?: number; // סך כל התשלום החודשי
  has_life_insurance?: boolean; // האם יש ביטוח חיים משכנתא
  life_insurance_policy_number?: string; // מספר פוליסת ביטוח חיים
  property_value?: number; // שווי הנכס
  equity?: number; // הון עצמי
  property_type?: PropertyType; // סוג הנכס
  total_prepayment_fees?: number; // סה"כ עמלות לסילוק ההלוואה
}

/**
 * פרטי משכנתא שחולצו ממסמך (AI Extraction Output)
 * מכיל שדות נוספים לחילוץ AI שלא נשמרים ב-DB
 * תואם ל-MortgageDetailsExtracted בצד הפייתון
 */
export interface MortgageDetailsExtracted extends MortgageDetails {
  // AI Extraction specific fields
  core_summary?: string; // סיכום מילולי של המסמך
  core_highlights?: string[]; // נקודות עיקריות

  // Borrower information (israeli_id not persisted, only for validation)
  borrowers?: MortgageBorrower[]; // לווים (כולל ת"ז לולידציה בלבד)
}

/**
 * ברירת מחדל למסלול משכנתא ריק
 */
export const defaultMortgageTrack = (): MortgageTrack => ({
  track_type: MortgageTrackType.FIXED_LINKED,
  amountPercentage: 0,
  amount: 0,
  months: 0,
  interest_rate: 0,
  repayment_type: MortgageTrackRepaymentType.SPITZER,
  indexation_type: MortgageTrackIndexationType.CPI,
  interest_base_type: MortgageInterestBaseType.FIXED,
});

/**
 * ברירת מחדל למשכנתא ריקה
 */
export const defaultMortgageDetails = (): MortgageDetails => ({
  mortgage_number: "",
  property_address: "",
  total_original_amount: 0,
  total_current_balance: 0,
  tracks: [defaultMortgageTrack()],
  monthly_payment_total: 0,
  has_life_insurance: false,
});